import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  FlexGrid,
  FlexGridRow,
  FlexGridCol,
  StackView,
  Button,
  TextInput,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Feedback,
} from '@telus-uds/components-web';
import useAuth from '../../hooks/useAuth';
import { BACKEND_BASE_URL } from '../../constants/api';

interface Admin {
  email: string;
}

const RBAC_BASE_URL = `${BACKEND_BASE_URL}/api/rbac`;

export const AdminPage = () => {
  const { jwt, isLoading: authLoading } = useAuth();

  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const emailError = emailTouched && newEmail.trim() && !isValidEmail(newEmail) ? 'Enter a valid email address' : undefined;

  const getHeaders = useCallback(
    (): Record<string, string> => ({
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    }),
    [jwt],
  );

  const fetchAdmins = useCallback(async () => {
    const res = await fetch(`${RBAC_BASE_URL}/admins`, { headers: getHeaders() });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    setAdmins(data.admins);
  }, [getHeaders]);

  useEffect(() => {
    if (authLoading || !jwt) return;
    (async () => {
      try {
        const res = await fetch(`${RBAC_BASE_URL}/me/is-admin`, { headers: getHeaders() });
        const data = await res.json();
        setIsAdmin(data.isAdmin);
        if (data.isAdmin) await fetchAdmins();
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, jwt, getHeaders, fetchAdmins]);

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email) return;
    setAdding(true);
    setError('');
    try {
      const res = await fetch(`${RBAC_BASE_URL}/admins`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      setNewEmail('');
      setEmailTouched(false);
      await fetchAdmins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add admin');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email: string) => {
    setError('');
    try {
      const res = await fetch(`${RBAC_BASE_URL}/admins/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      await fetchAdmins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove admin');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <FlexGrid>
          <FlexGridRow>
            <FlexGridCol xs={12}>
              <Typography block>Loading...</Typography>
            </FlexGridCol>
          </FlexGridRow>
        </FlexGrid>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: '24px' }}>
        <FlexGrid>
          <FlexGridRow>
            <FlexGridCol xs={12}>
              <StackView space={{ md: 1 }}>
                <Typography block variant={{ size: 'h2' }}>Access denied</Typography>
                <Typography block variant={{ size: 'body' }}>
                  You need the <code>simplify:admin</code> role to view this page.
                </Typography>
              </StackView>
            </FlexGridCol>
          </FlexGridRow>
        </FlexGrid>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <FlexGrid>
        <FlexGridRow>
          <FlexGridCol xs={12} md={8}>
            <StackView space={{ md: 4 }}>
              <StackView space={{ md: 1 }}>
                <Typography block variant={{ size: 'h1' }}>Admin Management</Typography>
                <Typography block variant={{ size: 'body' }}>
                  Users with the <code>simplify:admin</code> role. Admins can add and remove other admins.
                </Typography>
              </StackView>

              <form
                onSubmit={e => {
                  e.preventDefault();
                  if (!adding && isValidEmail(newEmail)) handleAdd();
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      label="Add admin"
                      value={newEmail}
                      onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
                        setNewEmail(typeof value === 'string' ? value : value.target.value);
                      }}
                      onBlur={() => setEmailTouched(true)}
                      feedback={emailError}
                      feedbackType={emailError ? 'error' : undefined}
                    />
                  </div>
                  <Button
                    variant={{ priority: 'high' }}
                    onPress={handleAdd}
                    disabled={adding || !isValidEmail(newEmail)}
                  >
                    {adding ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </form>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.length === 0 ? (
                    <TableRow>
                      <TableCell>No admins yet</TableCell>
                      <TableCell> </TableCell>
                    </TableRow>
                  ) : (
                    admins.map(admin => (
                      <TableRow key={admin.email}>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>
                          <Button
                            variant={{ danger: true, size: 'small' }}
                            onPress={() => handleRemove(admin.email)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {error && (
                <Feedback variant="danger">
                  <Typography block variant={{ size: 'smallBody' }}>{error}</Typography>
                </Feedback>
              )}
            </StackView>
          </FlexGridCol>
        </FlexGridRow>
      </FlexGrid>
    </div>
  );
};

export default AdminPage;
